import { useEffect, useMemo, useRef, useState } from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import * as Sentry from '@sentry/react';

import { get as getUser } from 'js/lib/user';

import GetSkillAreaCourseItemsQuery, {
  type GetSkillAreaCourseItemsQueryData,
  type GetSkillAreaCourseItemsQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetSkillAreaCourseItems';
import GetSkillExpressionAssessmentMappingsByAssessmentIdQuery from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/GetSkillExpressionAssessmentMappingsByAssessmentId.graphql';
import type { SkillAttainmentSkillAreaAttainmentFragment } from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillAttainmentByOccupationGroupAndCareerBandQuery';
import type {
  GetSkillExpressionAssessmentMappingsByAssessmentIdQuery as GetSkillExpressionAssessmentMappingsByAssessmentIdQueryData,
  GetSkillExpressionAssessmentMappingsByAssessmentIdQueryVariables,
} from 'bundles/skills-common/shared/verified-skills/data/skill-pathway/__generated__/GetSkillExpressionAssessmentMappingsByAssessmentId';
import useGetSkillAssessmentIdAndPassingFraction from 'bundles/skills-common/shared/verified-skills/hooks/useGetSkillAssessmentIdAndPassingFraction';
import {
  getIsSkillAssessmentItem,
  getSkillPassingThresholdForAssessment,
} from 'bundles/skills-common/shared/verified-skills/utils/verifiedSkillsUtils';

type Props = {
  skillAttainmentForCourse?: SkillAttainmentSkillAreaAttainmentFragment;
  courseId?: string;
};

/**
 * Custom hook that calculates the total and passing threshold for skill assessments.
 * Fetches thresholds for all assessments and calculates aggregated totals using
 * getSkillPassingThresholdForAssessment.
 * Use this hook when you need to fetch mastery details for a skill area with some already mastered skills. No mastered skills means the course might not be enrolled in yet.
 *
 * This is used inside the Skill Dialog and so must not rely on computed item data as Skill Dialogs are also rendered inside Enterprise pages.
 *
 * @param skillAttainmentForCourse - The skill attainment for the course.
 * @param courseId - The course ID.
 * @returns The skill mastery details. Note: `error` is only populated for unexpected/catastrophic failures (e.g., GraphQL errors).
 *          Item-level fetch failures are handled transparently by returning `skillAttainmentThreshold: 0`,
 *          which causes the component to fall back to SkillProgressBar. Individual failures are logged to Sentry.
 */
export const useGetSkillAttainmentThresholdForSkillArea = ({ skillAttainmentForCourse, courseId }: Props) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [threshold, setThreshold] = useState<number>(0);

  const userId = getUser().id;

  const {
    data,
    error: itemsError,
    loading: itemsLoading,
  } = useQuery<GetSkillAreaCourseItemsQueryData, GetSkillAreaCourseItemsQueryVariables>(GetSkillAreaCourseItemsQuery, {
    variables: {
      courseId: courseId || '',
      learnerId: `${userId}` || '',
    },
    fetchPolicy: 'cache-first',
  });

  const items = data?.OnDemandCourseMaterialsV2?.learner?.linked?.onDemandCourseMaterialItemsV2;

  const skillAssessmentItems = useMemo(
    () =>
      items
        ?.filter((item) =>
          getIsSkillAssessmentItem(
            item.contentSummary?.typeName,
            item.contentSummary?.definition?.assignmentFeatures ?? undefined
          )
        )
        .map((item) => ({
          itemId: item?.id || '',
          itemTypeName: item?.contentSummary?.typeName,
          passingFraction: item?.contentSummary?.definition?.passingFraction,
        })) || [],
    [items]
  );

  const { fetchSkillAssessmentIdAndPassingFraction } = useGetSkillAssessmentIdAndPassingFraction();

  const [fetchSkillExpressionMappings] = useLazyQuery<
    GetSkillExpressionAssessmentMappingsByAssessmentIdQueryData,
    GetSkillExpressionAssessmentMappingsByAssessmentIdQueryVariables
  >(GetSkillExpressionAssessmentMappingsByAssessmentIdQuery, {
    context: { clientName: 'gatewayGql' },
    fetchPolicy: 'cache-first',
  });

  // Instead of disabling the rule entirely, be explicit about stable references
  const fetchSkillExpressionMappingsRef = useRef(fetchSkillExpressionMappings);
  const fetchSkillAssessmentIdAndPassingFractionRef = useRef(fetchSkillAssessmentIdAndPassingFraction);

  useEffect(() => {
    fetchSkillExpressionMappingsRef.current = fetchSkillExpressionMappings;
    fetchSkillAssessmentIdAndPassingFractionRef.current = fetchSkillAssessmentIdAndPassingFraction;
  });

  useEffect(() => {
    let isCancelled = false;

    const fetchAllPassingThresholds = async () => {
      // Don't set loading to false if the items query is still loading
      // because we don't know yet if there will be items
      if (itemsLoading) {
        return;
      }

      if (!skillAssessmentItems || skillAssessmentItems.length === 0) {
        setLoading(false);
        return;
      }

      setLoading(true);

      try {
        // Fetch assessment id + passing fraction by item type, then fetch skill expression mappings.
        // Use Promise.allSettled to handle failures per-item instead of failing everything
        const results = await Promise.allSettled(
          skillAssessmentItems.map(async ({ itemId, itemTypeName, passingFraction }) => {
            const {
              assessmentId,
              assessmentType,
              passingFraction: fetchedPassingFraction,
            } = await fetchSkillAssessmentIdAndPassingFractionRef.current({
              itemId,
              itemTypeName,
              courseId: courseId || '',
            });

            if (!assessmentId) {
              throw new Error('Failed to fetch assessment id');
            }

            const skillExpressionsMappingResult = await fetchSkillExpressionMappingsRef.current({
              variables: {
                assessmentId,
                assessmentType,
              },
            });

            return {
              itemId,
              passingFraction: passingFraction ?? fetchedPassingFraction ?? 0,
              skillExpressionMappings:
                skillExpressionsMappingResult?.data?.VerifiedSkillsMapping
                  ?.getSkillExpressionAssessmentMappingsByAssessmentId,
            };
          })
        );

        if (isCancelled) return;

        // Check if any items failed
        const hasFailedItems = results.some((result) => result.status === 'rejected');

        // If any item failed, set threshold to 0 to fall back to SkillProgressBar
        if (hasFailedItems) {
          // Log all failed items to Sentry
          results.forEach((result, index) => {
            if (result.status === 'rejected') {
              const { itemId } = skillAssessmentItems[index] ?? {};
              Sentry.captureException(result.reason, {
                extra: {
                  message: 'Failed to fetch skill mastery details for individual item',
                  courseId,
                  skillAreaId: skillAttainmentForCourse?.skillArea?.id,
                  itemId,
                },
              });
            }
          });
          // Set threshold to 0 so component falls back to SkillProgressBar
          setThreshold(0);
        } else {
          // Calculate totals for each assessment and aggregate (all items succeeded)
          type FulfilledResult = PromiseFulfilledResult<{
            itemId: string;
            passingFraction: number;
            skillExpressionMappings: NonNullable<
              GetSkillExpressionAssessmentMappingsByAssessmentIdQueryData['VerifiedSkillsMapping']
            >['getSkillExpressionAssessmentMappingsByAssessmentId'];
          }>;

          const calculatedSkillAttainmentThreshold = (results as FulfilledResult[]).reduce<number>((acc, result) => {
            const { passingFraction, skillExpressionMappings } = result.value;
            const mappedSkillExpressionIds = new Set(
              skillExpressionMappings?.map((mapping) => mapping.skillExpression.id) ?? []
            );

            const skillExpressionsAttainment =
              skillAttainmentForCourse?.skillExpressionsAttainment?.filter((attainment) =>
                mappedSkillExpressionIds.has(attainment?.skillExpression?.id ?? '')
              ) || [];

            const assessmentResult = getSkillPassingThresholdForAssessment({
              skillExpressionsAttainment,
              passingFraction,
            });

            return acc + assessmentResult.skillAttainmentThreshold;
          }, 0);

          setThreshold(calculatedSkillAttainmentThreshold);
        }
      } catch (err) {
        // This catch block should rarely be hit now since individual failures are handled above
        setThreshold(0);
        setError(err as Error);
        Sentry.captureException(err as Error, {
          extra: {
            message: 'Unexpected error in fetchAllPassingThresholds',
            courseId,
            skillAreaId: skillAttainmentForCourse?.skillArea?.id,
          },
        });
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchAllPassingThresholds();

    return () => {
      isCancelled = true;
    };
  }, [courseId, skillAttainmentForCourse, itemsLoading, skillAssessmentItems]);

  const isLoading = itemsLoading || loading;

  return {
    loading: isLoading,
    error: itemsError || error,
    skillAttainmentThreshold: isLoading ? 0 : threshold,
  };
};
